import { Controller } from "@hotwired/stimulus";

// Responsive flyout: a right-side drawer on tablet/desktop and a bottom sheet on
// mobile (positioning handled by md:/max-md: classes on the panel). Same element,
// two presentations — no parallel markup.
//
// The panel is always in the DOM, parked off-screen via a transform; toggling
// `.is-open` slides it in/out (the CSS transition animates both directions). On
// mobile the drag handle supports swipe-to-dismiss: drag the panel down past a
// threshold to close, otherwise it snaps back.
//
// Opens automatically when its Turbo frame finishes loading (the trigger is a plain
// link with data-turbo-frame="<frameId>"), so nested triggers don't need a
// reference here.
export default class extends Controller {
  static targets = ["backdrop", "panel"];
  static values = { frameId: { type: String, default: "invite_flyout" } };

  declare readonly backdropTarget: HTMLElement;
  declare readonly panelTarget: HTMLElement;
  declare readonly frameIdValue: string;

  private onFrameLoad!: (event: Event) => void;
  private onKeydown!: (event: KeyboardEvent) => void;
  private dragging = false;
  private dragStartY = 0;
  private dragDelta = 0;
  private scrollY = 0;

  connect() {
    this.onFrameLoad = (event: Event) => {
      if ((event.target as HTMLElement)?.id === this.frameIdValue) this.open();
    };
    this.onKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") this.close();
    };
    document.addEventListener("turbo:frame-load", this.onFrameLoad);
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.onFrameLoad);
    document.removeEventListener("keydown", this.onKeydown);
    this.unlockScroll();
  }

  open() {
    this.backdropTarget.classList.add("is-open");
    this.panelTarget.classList.add("is-open");
    this.lockScroll();
    document.addEventListener("keydown", this.onKeydown);
    // Move focus into the panel (e.g. the email input). preventScroll keeps the
    // slide-in animation from jumping the off-screen element into view.
    this.panelTarget
      .querySelector<HTMLElement>("[autofocus]")
      ?.focus({ preventScroll: true });
  }

  close() {
    if (!this.panelTarget.classList.contains("is-open")) return;
    this.backdropTarget.classList.remove("is-open");
    this.panelTarget.classList.remove("is-open");
    this.unlockScroll();
    document.removeEventListener("keydown", this.onKeydown);
  }

  // Lock the page behind the flyout. `overflow: hidden` on <body> is ignored by
  // iOS Safari for touch scrolling, so pin the body with `position: fixed` and
  // remember the scroll position to restore on close — the cross-browser way to
  // stop the background scrolling/rubber-banding under the sheet.
  private lockScroll() {
    const body = document.body;
    if (body.style.position === "fixed") return; // already locked; keep saved scrollY
    this.scrollY = window.scrollY;
    body.style.position = "fixed";
    body.style.top = `-${this.scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  }

  private unlockScroll() {
    const body = document.body;
    if (body.style.position !== "fixed") return;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    window.scrollTo(0, this.scrollY);
  }

  // Close after a successful submit (e.g. sending an email invite).
  onSubmit(event: CustomEvent) {
    if (event.detail?.success) this.close();
  }

  // ---- swipe-to-dismiss (mobile bottom sheet) ----

  dragStart(event: PointerEvent) {
    this.dragging = true;
    this.dragStartY = event.clientY;
    this.dragDelta = 0;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.panelTarget.style.transition = "none";
  }

  dragMove(event: PointerEvent) {
    if (!this.dragging) return;
    // Only allow dragging downward; ignore upward movement.
    this.dragDelta = Math.max(0, event.clientY - this.dragStartY);
    this.panelTarget.style.transform = `translateY(${this.dragDelta}px)`;
  }

  dragEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.panelTarget.style.transition = "";
    const threshold = Math.min(this.panelTarget.offsetHeight * 0.3, 140);
    // Clearing the inline transform lets the CSS class transform take over; if we're
    // closing, removing .is-open animates from the dragged position down to off-screen.
    this.panelTarget.style.transform = "";
    if (this.dragDelta > threshold) this.close();
  }
}
